import { AxiosRequestConfig } from "axios";
import { callExternalApi, ApiResponse } from "./external-api.service";
import { getLogger } from "src/utils/logger";

const logger = getLogger('auth0.service');

// Auth0 configuration from environment variables
const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

export interface Auth0PasswordResetRequest {
  email: string;
  connection?: string;
}

export interface Auth0PasswordResetResponse {
  message: string;
}

/**
 * Send a password reset email using Auth0's Authentication API
 *
 * @param email The email address to send the password reset link to
 * @param connection The Auth0 connection name (defaults to "Username-Password-Authentication")
 * @returns A promise resolving to an ApiResponse containing the result or an error
 */
export const sendPasswordResetEmail = async (
  email: string,
  connection: string = "Username-Password-Authentication"
): Promise<ApiResponse<Auth0PasswordResetResponse>> => {
  if (!auth0Domain || !auth0ClientId) {
    logger.error("Auth0 configuration missing", { auth0Domain: !!auth0Domain, auth0ClientId: !!auth0ClientId });
    return {
      data: null,
      error: { message: "Auth0 configuration is missing. Please check environment variables." }
    };
  }

  const config: AxiosRequestConfig = {
    url: `https://${auth0Domain}/dbconnections/change_password`,
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    data: {
      client_id: auth0ClientId,
      email: email,
      connection: connection
    },
  };
  
  logger.debug("Sending password reset email", { email, connection, domain: auth0Domain });
  
  try {
    const response = await callExternalApi({ config });
    logger.debug(`Password reset response received`, { hasData: !!response.data, hasError: !!response.error });
    
    if (response.error) {
      // Check for CORS-related errors
      if (response.error.message.includes('CORS') || response.error.message.includes('Access-Control')) {
        return {
          data: null,
          error: { 
            message: "CORS error: Please ensure your Auth0 application has the correct Allowed Origins configured. Add your domain to the Allowed Origins (CORS) list in Auth0 Dashboard > Applications > Applications."
          }
        };
      }
      
      return {
        data: null,
        error: response.error
      };
    }
    
    if (!response.data) {
      return {
        data: null,
        error: { message: "No response data received from Auth0" }
      };
    }
    
    // Auth0's change_password endpoint returns a simple success message
    return {
      data: {
        message: "Password reset email sent successfully"
      },
      error: null
    };
  } catch (error) {
    logger.error("Unexpected error in sendPasswordResetEmail", error);
    return {
      data: null,
      error: { message: "An unexpected error occurred while sending the password reset email" }
    };
  }
};
